import { useEffect, useRef, useState } from "react";
import TitleBar from "../../designSystem/c-organism/titleBar/TitleBar";
import { useSelector } from "react-redux";
import { ref, onValue, push, get } from "firebase/database";
import { db } from "../../configs/Firebase";
import { Route, Routes, useNavigate } from "react-router-dom";
import List from "../../designSystem/b-molecules/lists/List";
import Journey from "./Journey";
import Dialog from "../../designSystem/c-organism/dialogs/Dialog";
import Input from "../../designSystem/b-molecules/formElements/input/Input";
import Button from "../../designSystem/b-molecules/buttons/Button";
import style from "./Journeys.module.css";
import Select from "../../designSystem/b-molecules/formElements/select/Select";
import Tabs from "../../designSystem/b-molecules/tabs/Tabs";
import { calculateJourneyScore, calculateStepScoreFinal } from "./utils/scoringUtils";
import { canEditSection, SECTIONS } from "../../utils/permissions";

const Journeys = () => {
  const org = useSelector((state) => state.orgStatus.value);
  const userStatus = useSelector((state) => state.userStatus.value);
  const [user, setUser] = useState(false);
  const [actions, setActions] = useState([]);
  const [journeys, setJourneys] = useState([]);
  const [journeysToPrint, setJourneysToPrint] = useState([]);

  const navigate = useNavigate();
  const [selectedLabels, setSelectedLabels] = useState([]);
  // const journeyFilters = JSON.parse(localStorage.getItem("journeyFilters"));
  const journeyFilters = {};
  const [canEdit, setCanEdit] = useState(false);
  const [viewMode, setViewMode] = useState('active');
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  // const journeyFilters = [];

  useEffect(() => {
    if (canEdit) {
      setActions([{ icon: "add_circle", name: "Journey", action: createNew }]);
    } else {
      setActions([]);
    }
  }, [canEdit]);

  useEffect(() => {
    if (user && org.payload) {
      setCanEdit(canEditSection(user, org.payload, SECTIONS.JOURNEYS));
    } else {
      setCanEdit(false);
    }
  }, [user, org.payload]);

  useEffect(() => {
    if (userStatus) {
      setUser(JSON.parse(userStatus.payload));
    }
  }, [userStatus]);

  useEffect(() => {
    setSelectedLabels(journeyFilters);
  }, []);

  function onClickHandler(i) {
    navigate(i);
  }

  useEffect(() => {
    if (org.payload && org.payload !== undefined) {
      onValue(ref(db, "userData/" + org.payload + "/journeys"), (snapshot) => {
        let data = snapshot.val();
        let tempJourneys = [...journeys];
        let tempData = [];

        // First, create journey items without owners
        for (let i in data) {
          tempData.push({
            id: i,
            ...data[i],
            owners: [], // Initialize empty, will be populated below
          });
        }

        // Load owners and calculate journey scores for each journey
        Promise.all(
          tempData.map(async (journeyItem) => {
            try {
              const ownersRef = ref(db, `userData/${org.payload}/journeys/${journeyItem.id}/owners`);
              const ownersSnapshot = await get(ownersRef);
              const ownersData = ownersSnapshot.val();

              if (ownersData) {
                const ownerIds = Object.keys(ownersData);

                // Fetch profile data for each owner
                const profilePromises = ownerIds.map(async (ownerId) => {
                  try {
                    const profileSnapshot = await get(ref(db, `userData/${ownerId}/profile`));
                    const profile = profileSnapshot.val() || {};

                    const displayName = profile.displayName || "";
                    const initials = displayName
                      ? displayName.split(" ")[0].charAt(0) +
                      (displayName.split(" ")[1] ? displayName.split(" ")[1].charAt(0) : "")
                      : "";

                    return {
                      uid: ownerId,
                      name: displayName,
                      email: profile.email || "",
                      photoURL: profile.microsoftPhotoURL || null,
                      initials: initials,
                    };
                  } catch (error) {
                    console.error(`Error loading profile for owner ${ownerId}:`, error);
                    return null;
                  }
                });

                const profiles = await Promise.all(profilePromises);
                journeyItem.owners = profiles.filter(p => p !== null);
              }

              // Calculate journey score if journey has scoring rows
              if (journeyItem.rows) {
                const scoringRows = Object.values(journeyItem.rows).filter(row => row.type === "scoring");
                if (scoringRows.length > 0) {
                  try {
                    const journeyRefURL = `userData/${org.payload}/journeys/${journeyItem.id}`;
                    const scoringPath = `${journeyRefURL}/scoring`;
                    const scoringRef = ref(db, scoringPath);
                    const scoringSnapshot = await get(scoringRef);

                    if (scoringSnapshot.exists()) {
                      const scoringData = scoringSnapshot.val();
                      const stepScores = [];

                      // Collect all StepScoreFinal values from scoring cells
                      for (const rowId in scoringData) {
                        for (const cellId in scoringData[rowId]) {
                          const evaluations = scoringData[rowId][cellId].evaluations;
                          if (evaluations) {
                            const evaluationsList = Object.keys(evaluations).map(evalId => ({
                              id: evalId,
                              ...evaluations[evalId]
                            }));
                            const stepScoreFinal = calculateStepScoreFinal(evaluationsList);
                            if (stepScoreFinal !== null) {
                              stepScores.push(stepScoreFinal);
                            }
                          }
                        }
                      }

                      const journeyScoreValue = calculateJourneyScore(stepScores);
                      journeyItem.journeyScore = journeyScoreValue;
                    } else {
                      journeyItem.journeyScore = null;
                    }
                  } catch (error) {
                    console.error(`Error calculating journey score for journey ${journeyItem.id}:`, error);
                    journeyItem.journeyScore = null;
                  }
                } else {
                  journeyItem.journeyScore = null;
                }
              } else {
                journeyItem.journeyScore = null;
              }
            } catch (error) {
              console.error(`Error loading owners for journey ${journeyItem.id}:`, error);
            }

            return journeyItem;
          })
        ).then((journeysWithOwners) => {
          tempJourneys[org.payload] = {
            orgid: org.payload,
            items: journeysWithOwners,
          };
          setJourneys(tempJourneys);
        });
      });
    }
  }, [org]);

  const [journeyToTest, setJourneyToTest] = useState([]);
  useEffect(() => {
    var tempJourneysToPrint = [];
    for (let i in journeys) {
      tempJourneysToPrint.push(journeys[i]);
    }
    setJourneyToTest(tempJourneysToPrint);
  }, [journeys]);

  useEffect(() => {
    var tempJourneysToPrint = [];

    // if (selectedLabels) {
    //   for (let i in journeys) {
    //     let tempJourney = { ...journeys[i] };
    //     tempJourney.items = [];
    //     for (let y in journeys[i].items) {
    //       if (journeys[i].items[y].labels) {
    //         let tempLabels = [];
    //         for (let x in journeys[i].items[y].labels) {
    //           tempLabels.push({
    //             key: x,
    //             value: journeys[i].items[y].labels[x].value,
    //           });
    //           journeys[i].items[y].labels[x].selected = false;
    //         }

    //         let exists = selectedLabels.some((item2) => {
    //           return tempLabels.find((item1) => item1.value === item2.value);
    //         });

    //         if (exists) {
    //           let matchingIndices = tempLabels.reduce(
    //             (indices, item1, index) => {
    //               if (
    //                 selectedLabels.findIndex(
    //                   (item2) =>
    //                     item2.key === item1.key && item2.value === item1.value
    //                 ) !== -1
    //               ) {
    //                 indices.push(index);
    //               }
    //               return indices;
    //             },
    //             []
    //           );

    //           for (let x in matchingIndices) {
    //             journeys[i].items[y].labels[
    //               tempLabels[matchingIndices[x]].key
    //             ].selected = true;
    //           }

    //           tempJourney.items.push(journeys[i].items[y]);
    //         }
    //       } else {
    //         let exists2 = selectedLabels.some((item2) => {
    //           return item2.value === "No Label";
    //         });
    //         if (exists2) {
    //           tempJourney.items.push(journeys[i].items[y]);
    //         }
    //       }
    //     }
    //     tempJourneysToPrint.push(tempJourney);
    //   }
    // }else{
    // for (let i in journeys) {
    //   let tempJourney = { ...journeys[i] };
    //   tempJourneysToPrint.push(tempJourney);
    // }
    // }
    // tempJourneysToPrint.modified = Date.now();
    // setJourneysToPrint(tempJourneysToPrint);



    //temp
    for (let i in journeys) {
      let tempJourney = { ...journeys[i] };
      for (let y in tempJourney.items) {
        for (let n in tempJourney.items[y].labels) {
          tempJourney.items[y].labels[n].selected = true;
        }
      }
      tempJourneysToPrint.push(tempJourney);
    }

    // Sort journeys by labels
    // For each organization's journey list, sort the journeys by their labels
    tempJourneysToPrint.forEach((orgJourney) => {
      if (orgJourney.items && orgJourney.items.length > 0) {
        orgJourney.items.sort((journeyA, journeyB) => {
          // Get label values for journey A
          const labelsA = journeyA.labels
            ? Object.values(journeyA.labels)
              .map(label => (label.value || '').toLowerCase())
              .filter(val => val !== '')
              .sort()
            : [];

          // Get label values for journey B
          const labelsB = journeyB.labels
            ? Object.values(journeyB.labels)
              .map(label => (label.value || '').toLowerCase())
              .filter(val => val !== '')
              .sort()
            : [];

          // If journey A has no labels, put it at the end
          if (labelsA.length === 0 && labelsB.length > 0) return 1;
          // If journey B has no labels, put it at the end
          if (labelsB.length === 0 && labelsA.length > 0) return -1;
          // If both have no labels, sort by name
          if (labelsA.length === 0 && labelsB.length === 0) {
            return (journeyA.name || '').localeCompare(journeyB.name || '');
          }

          // Compare first labels alphabetically
          const firstLabelA = labelsA[0] || '';
          const firstLabelB = labelsB[0] || '';

          // If first labels are the same, compare by journey name
          if (firstLabelA === firstLabelB) {
            return (journeyA.name || '').localeCompare(journeyB.name || '');
          }

          return firstLabelA.localeCompare(firstLabelB);
        });

        // Filter journeys based on viewMode (active or archive)
        if (viewMode === 'active') {
          orgJourney.items = orgJourney.items.filter(j => !j.archived);
        } else if (viewMode === 'archive') {
          orgJourney.items = orgJourney.items.filter(j => j.archived === true);
        }

        // Filter by brand if selected
        if (selectedBrand) {
          orgJourney.items = orgJourney.items.filter(j => {
            const journeyBrandIds = j.brandIds || [];
            return journeyBrandIds.includes(selectedBrand);
          });
        }

        // Filter by search term if provided
        if (searchTerm) {
          // Split search term by spaces and filter out empty strings
          const searchTerms = searchTerm.trim().split(/\s+/).filter(term => term.length > 0);

          if (searchTerms.length > 0) {
            orgJourney.items = orgJourney.items.filter(j => {
              // Create combined searchable text from title, labels, and owners
              const titleText = (j.name || '').toLowerCase();

              const labelTexts = j.labels
                ? Object.values(j.labels)
                  .map(label => (label.value || '').toLowerCase())
                  .join(' ')
                : '';

              const ownerTexts = j.owners
                ? j.owners
                  .map(owner =>
                    ((owner.name || '') + ' ' + (owner.email || '')).toLowerCase().trim()
                  )
                  .join(' ')
                : '';

              const combinedText = `${titleText} ${labelTexts} ${ownerTexts}`.toLowerCase();

              // Check that ALL search terms appear in the combined text
              return searchTerms.every(term =>
                combinedText.includes(term.toLowerCase())
              );
            });
          }
        }
      }
    });

    tempJourneysToPrint.modified = Date.now();
    setJourneysToPrint(tempJourneysToPrint);
    // end temp
  }, [selectedLabels, journeys, viewMode, selectedBrand, searchTerm]);






  const [labels, setLabels] = useState({ count: 1 });
  useEffect(() => {
    // let tempLabels = [{ key: "none", value: "No Label", selected: false }];
    // if (journeyToTest.length) {
    //   for (let i in journeyToTest[0].items) {
    //     if (journeyToTest[0].items[i].labels) {
    //       for (let y in journeyToTest[0].items[i].labels) {
    //         let selected = true;
    //         tempLabels[y] = {
    //           key: y,
    //           value: journeyToTest[0].items[i].labels[y].value,
    //           selected: selected,
    //         };
    //       }
    //     }
    //   }
    //   let tempLabelsArr = [];
    //   tempLabelsArr[org.payload] = [];
    //   for (let i in tempLabels) {
    //     tempLabelsArr[org.payload].push({
    //       ...tempLabels[i],
    //     });
    //   }
    //   if (journeyFilters && journeyFilters.length > 0) {
    //     for (let i in tempLabelsArr[org.payload]) {
    //       tempLabelsArr[org.payload][i].selected = false;
    //       for (let y in journeyFilters) {
    //         if (tempLabelsArr[org.payload][i].value === journeyFilters[y].value) {
    //           tempLabelsArr[org.payload][i].selected = true;
    //         }
    //       }
    //     }
    //   }else{
    //     for (let i in tempLabelsArr[org.payload]) {
    //       tempLabelsArr[org.payload][i].selected = true;
    //     }
    //   }
    //   setLabels(tempLabelsArr);
    // }
  }, [journeyToTest]);

  const [showNewDialog, setShowNewDialog] = useState(false);
  const inputRef = useRef();
  function createNew() {
    setShowNewDialog(true);
  }
  function closeDialog() {
    setShowNewDialog(false);
    setNewName("");
    setError("");
  }
  const [newName, setNewName] = useState("");
  function onNewNameChange(e) {
    setError("");
    setNewName(e.target.value);
  }
  const [error, setError] = useState("");
  function saveNew() {
    if (!newName) {
      setError("Please provide a name for the journey");
    } else {
      //create the journey and redirect to it
      push(ref(db, "userData/" + org.payload + "/journeys"), {
        name: newName,
        brandIds: [],
      }).then((snap) => {
        setShowNewDialog(false);
        setNewName("");
        setError("");
        onClickHandler(snap.key);
      });
    }
  }

  function filtersChangeHandler(e, index) {
    // let tempLabels = labels;
    // tempLabels.count = Date.now();
    // tempLabels[org.payload][index].selected = e.target.checked;
    // setLabels(tempLabels);
  }

  function onSelecteOnly(e, index) {
    // let tempLabels = [...labels];
    // for (let i in tempLabels[org.payload]) {
    //   tempLabels[org.payload][i].selected = false;
    // }
    // tempLabels[org.payload][index].selected = true;
    // setLabels(tempLabels);
  }

  function onSearchChange(e) {
    setSearchTerm(e.target.value);
  }

  useEffect(() => {
    // console.log(labels);
    // if (labels[org.payload] && labels[org.payload].length) {
    //   let tempSelectedLabels = [];
    //   tempSelectedLabels[[org.payload]] = [];
    //   for (let i in labels[org.payload]) {
    //     if (labels[org.payload][i].selected) {
    //       tempSelectedLabels.push(labels[org.payload][i]);
    //     }
    //   }
    //   console.log(tempSelectedLabels)
    //   localStorage.setItem(
    //     "journeyFilters",
    //     JSON.stringify(tempSelectedLabels)
    //   );
    //   setSelectedLabels(tempSelectedLabels);
    // }
  }, [labels]);

  return (
    <Routes>
      <Route path="/">
        <Route
          index
          element={
            <div className="mainContentInner">
              <TitleBar
                title="Journeys"
                actions={actions}
                breadcrumb={[{ name: "Home", url: "/" }]}
              />
              <div className="contentPadding">
                <div className={style.filters}>
                  <div className={style.viewToggle}>
                    <Tabs
                      variants={["toggle"]}
                      tabs={[
                        {
                          name: "Active",
                          selected: viewMode === 'active',
                          onClick: () => setViewMode('active'),
                        },
                        {
                          name: "Archive",
                          selected: viewMode === 'archive',
                          onClick: () => setViewMode('archive'),
                        },
                      ]}
                    />
                  </div>
                  <div className={style.searchBar}>
                    <Input
                      type="text"
                      placeholder="Search journeys..."
                      label="Search"
                      value={searchTerm}
                      onChange={onSearchChange}
                    />
                  </div>
                </div>
                {brands.length > 0 && (
                  <div className={style.filters}>
                    <Select
                      type="dropdown"
                      label="Filter by Brand"
                      options={[{ value: "All Brands" }, ...brands.map(b => ({ value: b.name }))]}
                      selectedOptions={[selectedBrand ? brands.find(b => b.id === selectedBrand)?.name || "All Brands" : "All Brands"]}
                      onChange={(e, index) => {
                        if (index === 0) {
                          setSelectedBrand(null);
                        } else {
                          setSelectedBrand(brands[index - 1].id);
                        }
                      }}
                      multiselect={false}
                    />
                  </div>
                )}
                {journeysToPrint.length &&
                  journeysToPrint[0].items &&
                  journeysToPrint[0].items.length ? (
                  <>
                    <List data={journeysToPrint} onClick={onClickHandler} />
                  </>
                ) : (
                  <div className={style.noJourneys}>
                    {viewMode === 'archive'
                      ? "No archived journeys yet."
                      : "Here's where the journey starts. Click the journey button in the top right to get started or click the dropdown in the top right to change workspace (if available)"
                    }
                  </div>
                )}
              </div>
              <Dialog
                show={showNewDialog}
                title="Create Journey"
                onClose={closeDialog}
              >
                <div className={style.newDialogContent}>
                  <Input
                    onChange={onNewNameChange}
                    type="text"
                    placeholder=""
                    label="Journey Name"
                    error={error}
                    value={newName}
                  />
                  <Button onClick={saveNew}>Create</Button>
                </div>
              </Dialog>
            </div>
          }
        />
        <Route path=":id" element={<Journey />} />
      </Route>
    </Routes>
  );
};

export default Journeys;
